import * as maplibregl from 'maplibre-gl';
import type { MapInfo } from '../../api/types';
import { theme, type Theme } from '../../stores/theme';
import { palette } from '../../utils/palette';
import { get } from 'svelte/store';

// styleFor is the map style for a theme: the server's recolored style, or a
// plain background in the theme's base color when the map proxy is off.
export function styleFor(info: MapInfo | null, t: Theme = get(theme)): string | maplibregl.StyleSpecification {
  if (info?.available) return t === 'dark' ? info.dark_style_url : info.light_style_url;
  return { version: 8, sources: {}, layers: [{ id: 'bg', type: 'background', paint: { 'background-color': palette('base') } }] };
}

// northUpMap makes a map that stays north-up and flat: maps have no rotation
// or tilt (spec 009 FR-011), so no gesture or key may turn or tilt them.
export function northUpMap(options: maplibregl.MapOptions): maplibregl.Map {
  const map = new maplibregl.Map({ ...options, dragRotate: false, pitchWithRotate: false, touchPitch: false, maxPitch: 0 });
  map.touchZoomRotate.disableRotation();
  map.keyboard.disableRotation();
  return map;
}

// followTheme runs onStyle whenever the map has a style to draw on, first and
// after every theme change, and swaps the style when the theme changes.
// setStyle drops the sources and layers a page added, so onStyle adds them
// again and redraws; the camera and HTML markers stay. The
// returned function stops following.
export function followTheme(map: maplibregl.Map, info: MapInfo | null, onStyle: () => void): () => void {
  map.on('style.load', onStyle);
  let first = true;
  const stop = theme.subscribe((t) => {
    if (first) {
      first = false;
      return;
    }
    // Without diff: false, MapLibre patches the style in place, skips
    // style.load, and leaves the page's overlays gone.
    map.setStyle(styleFor(info, t), { diff: false });
  });
  return () => {
    stop();
    map.off('style.load', onStyle);
  };
}
